import { z } from 'zod';

export const PAYROLL_STATUSES = ['draft', 'processing', 'processed', 'approved', 'paid'] as const;
export const payrollStatusEnum = z.enum(PAYROLL_STATUSES);
export type PayrollStatus = z.infer<typeof payrollStatusEnum>;

export const PAYROLL_ENTRY_STATUSES = ['draft', 'approved', 'paid'] as const;
export const payrollEntryStatusEnum = z.enum(PAYROLL_ENTRY_STATUSES);
export type PayrollEntryStatus = z.infer<typeof payrollEntryStatusEnum>;

export const createPayrollSchema = z.object({
  month: z.coerce.number().int().min(1, 'Month must be between 1 and 12').max(12),
  year: z.coerce.number().int().min(2000).max(2100),
  workingDays: z.coerce.number().int().min(1).max(31).default(26),
  notes: z.string().trim().max(500).optional().or(z.literal('')),
});

export type CreatePayrollInput = z.infer<typeof createPayrollSchema>;

const lineItemSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60),
  amount: z.coerce.number().min(0).default(0),
});

/** Manual, per-employee adjustment to an already-computed entry — bonus/incentive/custom lines. Blocked once the entry is `paid` (enforced in the service). */
export const updatePayrollEntrySchema = z.object({
  bonus: z.coerce.number().min(0).optional(),
  incentive: z.coerce.number().min(0).optional(),
  otherEarnings: z.array(lineItemSchema).optional(),
  otherDeductions: z.array(lineItemSchema).optional(),
  adjustmentNote: z.string().trim().max(500).optional(),
});

export type UpdatePayrollEntryInput = z.infer<typeof updatePayrollEntrySchema>;

export const payrollListQuerySchema = z.object({
  year: z.coerce.number().int().optional(),
});

export type PayrollListQuery = z.infer<typeof payrollListQuerySchema>;

/** Dashboard/department-wise/statutory reports all take an optional month+year, defaulting to the current one when omitted. */
export const monthYearQuerySchema = z.object({
  month: z.coerce.number().int().min(1).max(12).optional(),
  year: z.coerce.number().int().optional(),
});

export type MonthYearQuery = z.infer<typeof monthYearQuerySchema>;

/** Trends/YTD reports take a year only. */
export const yearQuerySchema = z.object({
  year: z.coerce.number().int().optional(),
});

export type YearQuery = z.infer<typeof yearQuerySchema>;
